import React, { useState } from 'react';
import classnames from 'classnames';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import * as mobxReact from 'mobx-react';
import { CommonPopupBodyFooterLayout } from '../../../../../global/common-popup/common-popup-body-footer-layout';
import { DefaultCloseSubWindowButton } from '../../../../../global/sub-window/common/common-close-sub-window-buttons';
import DefaultButton from '../../../../../global/default-button/default-button';
import { OEToggle } from '../../../../../global/oe-toggle/oe-toggle';
import Text from '../../../../../global/text/text';
import './conf-passport-nav-form.scss';
import { FormField } from '../../../../../global/form/form-field/form-field';
import { CustomPageConfigurationType, NotLandingPageTypes } from '../../../../../../api/custom-pages.dao';
import { ChevronUpIcon } from '../../../../../global/icons/icons';
import { reorder } from '../helpers';
import { CustomFieldHidden } from './page-hidden-component';
import ConfPassNavFormVM from './conf-passport-nav-form.viewmodel';
import { APPLY_AND_CLOSE_TEXT, APPLY_TEXT, CANCEL_TEXT, loadingText } from '../../../../../../constants/common-labels.constants';
import { ProjectCSPNonceDerictives } from '../../../../../../constants/common.constants';

interface IConfPassNavPresentation {
    vm: ConfPassNavFormVM;
    intl: IntlShape;
}

interface IItemProps {
    page: CustomPageConfigurationType;
    intl: IntlShape;
    setLandingPage: (i: string) => void;
    onChangePageEnabledState: (id: string, value: boolean) => void;
}

const Item = ({ page, intl, setLandingPage, onChangePageEnabledState }: IItemProps) => {
    const { customPageId, enabled, label, landingPage, type } = page;
    const [isCollapsed, setCollapsed] = useState(false);
    const collapseChevronClasses = classnames({
        collapsed: isCollapsed,
        centered: !landingPage,
    });

    const toggleLabel = landingPage
        ? {
              id: 'oe.common.landingAssigned',
              description: 'Checkbox landing label text',
              defaultMessage: 'Assigned as Landing Page',
          }
        : {
              id: 'oe.common.notLanding',
              description: 'Checkbox landing label text',
              defaultMessage: 'Not Assigned as Landing Page',
          };

    const getNotLandingPages = () => {
        return (
            <label className='draggable-item__title disabled'>
                <span>{label}</span>
            </label>
        );
    };

    const id = (customPageId || type).toString();
    const getLandingPages = () => {
        return (
            <>
                <label onClick={() => setCollapsed(!isCollapsed)} className='draggable-item__title'>
                    <span>{label}</span> <ChevronUpIcon />
                </label>
                {!isCollapsed && (
                    <div className='draggable-item__list'>
                        <FormField className='m-bottom-15px'>
                            <div
                                className={classnames('website-custom__toggle', {
                                    'website-custom__toggle--disabled': landingPage,
                                })}
                            >
                                <OEToggle
                                    isSelected={enabled}
                                    onClick={() => onChangePageEnabledState(id, false)}
                                    isDisabled={landingPage}
                                />
                                <Text
                                    className='website-custom__toggle-label'
                                    message={`${page.label} is enabled`}
                                    id={`oe.defaultPage.${page.type}.enabled`}
                                    description='Label text'
                                />
                            </div>
                        </FormField>
                        <FormField className='m-bottom-20px'>
                            <div className='website-custom__toggle'>
                                <OEToggle
                                    isSelected={landingPage}
                                    isDisabled={!enabled || landingPage}
                                    onClick={() => setLandingPage(id)}
                                />
                                <Text message={intl.formatMessage({ ...toggleLabel })} />
                            </div>
                        </FormField>
                    </div>
                )}
            </>
        );
    };

    return (
        <div className={collapseChevronClasses}>
            <div className='draggable-item__content'>
                {!NotLandingPageTypes.includes(type) ? getLandingPages() : getNotLandingPages()}
            </div>
        </div>
    );
};

const composeDraggableId = (page: CustomPageConfigurationType) => {
    if (page.customPageId) {
        return `page_${page.customPageId}`;
    }
    return page.type;
};

const Body = mobxReact.observer((props: IConfPassNavPresentation) => {
    const { vm, intl } = props;
    const pages = vm.pagesList;
    const [sortableFields, staticFields] = pages.reduce(
        (prev, next) => {
            prev[next.enabled ? 0 : 1].push(next);
            return prev;
        },
        [[], []]
    );

    const list = sortableFields.slice().sort((prevPage, nexPaged) => prevPage.order - nexPaged.order);

    const onDragEnd = result => {
        if (!result.destination) {
            return;
        }

        const sorted = reorder(list, result.source.index, result.destination.index);

        vm.updatePagesOrder(sorted.concat(staticFields));
    };

    return (
        <div className='conf-pass-nav-form'>
            <div className='oe-form'>
                <DragDropContext onDragEnd={onDragEnd} nonce={ProjectCSPNonceDerictives.OEC_DRAG_DROP_STYLES}>
                    <Droppable droppableId='droppable'>
                        {droppableProps => (
                            <div {...droppableProps.droppableProps} ref={droppableProps.innerRef}>
                                {list.map((page, index) => {
                                    const id = composeDraggableId(page);
                                    return (
                                        <Draggable key={id} draggableId={id} index={index}>
                                            {draggableProps => {
                                                return (
                                                    <div
                                                        className='custom-field__wrapper'
                                                        ref={draggableProps.innerRef}
                                                        {...draggableProps.draggableProps}
                                                    >
                                                        <Item
                                                            page={page}
                                                            intl={intl}
                                                            setLandingPage={vm.setLandingPage}
                                                            onChangePageEnabledState={vm.onChangePageEnabledState}
                                                        />
                                                        <div className='custom-field__drag-icon'>
                                                            <i {...draggableProps.dragHandleProps} tabIndex={-1} />
                                                        </div>
                                                    </div>
                                                );
                                            }}
                                        </Draggable>
                                    );
                                })}
                                {droppableProps.placeholder}
                            </div>
                        )}
                    </Droppable>
                </DragDropContext>
            </div>
            {staticFields.map(page => (
                <CustomFieldHidden
                    page={page}
                    key={page.customPageId}
                    onChange={() => vm.onChangePageEnabledState((page.customPageId || page.type).toString(), true)}
                />
            ))}
        </div>
    );
});

const ConfPassNavPresentation = injectIntl(
    mobxReact.observer(({ vm, intl }: IConfPassNavPresentation) => {
        if (vm.isLoading) {
            return (
                <CommonPopupBodyFooterLayout
                    body={
                        <div className='oe-form conf-pass-nav-form'>
                            { loadingText() }
                        </div>
                    }
                />
            );
        }

        return (
            <CommonPopupBodyFooterLayout
                className={'hidden-scroll p-bottom-10px'}
                body={<Body vm={vm} intl={intl} />}
                footer={
                    <>
                        {vm.isSaving && (
                            <span className='oe-popup-layout__footer-status-area'>
                                <FormattedMessage
                                    id='oe.customFields.dataIsBeingSaved'
                                    description='Preloader message text'
                                    defaultMessage='Data is being saved, please wait{suffix}'
                                    values={{
                                        suffix: '...',
                                    }}
                                />
                            </span>
                        )}
                        <div className='buttons-block buttons-block--from-right'>
                            <DefaultCloseSubWindowButton
                                text={intl.formatMessage(APPLY_AND_CLOSE_TEXT)}
                                modifier={['fat', 'confirm-safe']}
                                onClick={() => vm.applyConfPassportNavForm()}
                            />
                            <DefaultButton
                                text={intl.formatMessage(APPLY_TEXT)}
                                modifier={['fat', 'confirm-safe']}
                                onClick={() => vm.applyConfPassportNavForm()}
                            />
                            <DefaultCloseSubWindowButton
                                text={intl.formatMessage(CANCEL_TEXT)}
                            />
                        </div>
                    </>
                }
            />
        );
    })
);

export { ConfPassNavPresentation };
